import { forwardRef, memo, useEffect, useRef, type Ref } from 'react';
import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { InputSearchOffice } from '~/components/PermanentJobsV2/components/InputSearchOffice/InputSearchOffice';
import { useUserSelector } from '~/hooks';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';

interface IOfficeFieldProps {
  value?: string;
  onChange?: (value: string) => void;
}

const Component = ({ value, onChange }: IOfficeFieldProps, ref: Ref<HTMLDivElement>) => {
  const isParentOffice = useIsParentOfficeSelector();
  const shouldShowDropdown = isParentOffice;

  const user = useUserSelector();
  const fallbackOfficeName = user.office_name;
  const fallbackOfficeId = user.id;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!shouldShowDropdown && fallbackOfficeId) {
      onChangeRef.current?.(fallbackOfficeId);
    }
  }, [shouldShowDropdown, fallbackOfficeId]);

  if (shouldShowDropdown) {
    return (
      <div ref={ref}>
        <Column data-testid="office-field" data-cy="office-field" gap={2}>
          <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
            Office:
          </Typography>
          <InputSearchOffice value={value} onChange={(v) => onChange?.(v)} />
        </Column>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Column data-testid="office-field" data-cy="office-field" gap={1}>
        <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
          Office:
        </Typography>
        <Typography
          as="span"
          size="body_large"
          color={colors.neutral[600]}
          numberOfLines={2}
          data-testid="office-static-pill"
          data-cy="office-static-pill"
        >
          {fallbackOfficeName}
        </Typography>
      </Column>
    </div>
  );
};

export const OfficeField = memo(forwardRef(Component));
